import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_CONFIG, API_ENDPOINTS } from '../config/apiConfig';
import Header from '../components/Header';
import Breadcrumb from '../components/Breadcrumb';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import ProductCard from '../components/ProductCard';

const ProductDetailPage = () => {
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState('/assets/img/product/big-product1.jpg');
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [variations, setVariations] = useState([]);

  const { id } = useParams();
  const productId = id;
  const cart = useCart();
  const wishlist = useWishlist();

  // Helper function to safely format prices
  const formatPrice = (price) => {
    const numPrice = parseFloat(price);
    return isNaN(numPrice) ? '0.00' : numPrice.toFixed(2);
  };



  // Check if product is in cart
  const isInCart = cart.isInCart(product?.id);
  const cartItem = isInCart ? cart.cart.find(item => item.id === product.id) : null;

  useEffect(() => {
    const fetchProduct = async () => {
      if (!productId) {
        setError('Product ID not provided');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const res = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.PRODUCTS.GET_BY_ID}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: Number(productId) })
        });
        if (!res.ok) throw new Error(`HTTP Error ${res.status}`);
        const json = await res.json();
        if (!json || !json.data) throw new Error('Invalid product data');
        const data = json.data;
        // Parse variations
        let parsedVariations = [];
        try {
          if (data.variations && data.variations !== '[]' && data.variations !== '') {
            parsedVariations = JSON.parse(data.variations);
            // Normalize field names: use 'title' as 'name' for consistency
            parsedVariations = parsedVariations.map(group => ({
              ...group,
              name: group.title,
              items: group.items.map(item => ({
                ...item,
                name: item.title,
                id: item.title // use title as id for selection
              }))
            }));
          }
        } catch (err) {
          console.warn('Failed to parse variations:', err);
          parsedVariations = [];
        }
        
        // Parse images
        let images = [];
        try {
          if (data.images && data.images !== '[]' && data.images !== '') {
            const parsed = JSON.parse(data.images);
            // Filter out empty strings
            images = parsed.filter(img => img && img.trim() !== '');
          }
        } catch (err) {
          console.warn('Failed to parse images:', err);
          images = [];
        }
        
        setVariations(parsedVariations);
        
        // Set default selected variation (first item of first variation group)
        // Process related products to ensure they have the required fields for ProductCard
        let processedRelated = [];
        if (json.related && Array.isArray(json.related)) {
          processedRelated = json.related.map(related => ({
            id: related.id,
            name: related.name || 'Unknown Product',
            original_price: parseFloat(related.original_price) || parseFloat(related.price) || 0,
            sell_price: parseFloat(related.sell_price) || parseFloat(related.price) || parseFloat(related.original_price) || 0,
            discount: parseFloat(related.discount) || 0,
            cover: related.cover || related.image || '',
            images: related.images || [],
            descriptions: related.descriptions || related.description || '',
            rating: related.rating || 0,
            reviews: related.total_rating || 0,
            in_stock: Number(related.in_stoke ?? related.in_stock ?? 1),
            // CRITICAL: Include size and variations for ProductCard variant modal
            size: related.size || '0',
            variations: related.variations || [],
            // Add other fields that ProductCard might need
            cate_id: related.cate_id,
            sub_cate_id: related.sub_cate_id,
            store_id: related.store_id
          }));
        }
        

        
        if (parsedVariations.length > 0 && parsedVariations[0].items && parsedVariations[0].items.length > 0) {
          setSelectedVariation(parsedVariations[0].items[0]);
          setSelectedVariantIndex(0);
        }
        // Build product object
        const prod = {
          id: data.id,
          name: data.name,
          cover: data.cover,
          images: images, // Already parsed array of image filenames
          original_price: parseFloat(data.original_price) || 0,
          sell_price: parseFloat(data.sell_price) || parseFloat(data.original_price) || 0,
          price: parseFloat(data.sell_price) || parseFloat(data.original_price) || 0, // Add price field
          oldPrice: parseFloat(data.original_price) || null, // Add oldPrice field
          discount: parseFloat(data.discount) || 0,
          rating: data.rating || 0,
          reviews: data.total_rating || 0,
          description: data.descriptions || '',
          // normalize in_stock to number (API uses `in_stoke`)
          in_stock: Number(data.in_stoke ?? data.in_stock ?? 0),
          // include feature & disclaimer fields
          key_features: data.key_features || null,
          disclaimer: data.disclaimer || null,
          soldby: json.soldby || null,
          related: processedRelated,
          have_pcs: Number(data.have_pcs),
          pcs: data.pcs,
          have_kg: Number(data.have_kg),
          kg: data.kg,
          have_gram: Number(data.have_gram),
          gram: data.gram,
          have_liter: Number(data.have_liter),
          liter: data.liter,
          have_ml: Number(data.have_ml),
          ml: data.ml,
          variations: parsedVariations,
          exp_date: data.exp_date,
          type_of: data.type_of,
          in_offer: data.in_offer,
          kind: data.kind,
          cate_id: data.cate_id,
          sub_cate_id: data.sub_cate_id,
          store_id: data.store_id
        };
        setProduct(prod);
        
        // Build image array for display: [cover, ...images]
        const displayImages = [];
        
        // Add cover as first image
        if (prod.cover && prod.cover.trim()) {
          displayImages.push(prod.cover.startsWith('http') 
            ? prod.cover 
            : `${API_CONFIG.IMAGE_URL}${prod.cover}`);
        }
        
        // Add additional images
        if (prod.images && prod.images.length > 0) {
          prod.images.forEach(img => {
            if (img && img.trim()) {
              displayImages.push(img.startsWith('http') 
                ? img 
                : `${API_CONFIG.IMAGE_URL}${img}`);
            }
          });
        }
        
        // Set selected image (first available or default)
        const firstImage = displayImages.length > 0 
          ? displayImages[0] 
          : '/assets/img/product/big-product1.jpg';
        setSelectedImage(firstImage);
      } catch (err) {
        console.error('Error fetching product details:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  // Using product state from API response



  // Normalize HTML line breaks (<br>, <br/>, <br />) to newline characters
  const normalizeBreaks = (text) => {
    if (!text && text !== 0) return '';
    try {
      return String(text).replace(/<br\s*\/?>(?=>)?/gi, '\n');
    } catch (e) {
      return String(text);
    }
  };



  // Basic HTML sanitizer: allow only a small subset of tags and strip attributes
  const sanitizeBasicHtml = (input) => {
    if (!input && input !== 0) return '';
    let text = String(input);
    // Remove script/style blocks
    text = text.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');
    text = text.replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '');

    // Convert literal backslash-n sequences (e.g. "\\n") to real newlines
    text = text.replace(/\\n/g, '\n');

    // Normalize BR to consistent tag first
    text = text.replace(/<br\s*\/?>/gi, '<br/>');

    // Remove ALL leading '>' characters - these are markdown-style quote indicators
    // Remove from start of string
    text = text.replace(/^>\s*/gm, '');
    // Remove after <br/> tags
    text = text.replace(/(<br\/>)\s*>\s*/gi, '$1');
    // Remove after any closing tag
    text = text.replace(/(<\/[^>]+>)\s*>\s*/gi, '$1');
    // Remove standalone > characters
    text = text.replace(/\n\s*>\s*/g, '\n');

    // If the input is plain text with headings separated by blank lines,
    // convert lines that are followed by an empty line into <h4> headings.
    const formatPlainTextHeaders = (src) => {
      const lines = src.split(/\r?\n/);
      const out = [];
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i] ? lines[i].trim() : '';
        const next = (lines[i + 1] || '').trim();
        // If line has no HTML tags, is shortish, and next line is empty -> heading
        if (
          line &&
          !/<[^>]+>/.test(line) &&
          line.length > 2 && line.length < 80 &&
          next === ''
        ) {
          out.push(`<h4>${line}</h4>`);
          i++; // skip the blank line
        } else {
          out.push(line);
        }
      }
      return out.join('\n');
    };

    text = formatPlainTextHeaders(text);

    // Normalize raw newlines to single \n
    text = text.replace(/\r\n|\r/g, '\n');
    // Convert newlines to <br/> so they render inside innerHTML
    text = text.replace(/\n/g, '<br/>');

    const allowed = ['b','strong','em','i','p','br','ul','ol','li','u','span','h1','h2','h3','h4'];
    // Strip any tag that's not in allowed; for allowed tags, remove attributes
    text = text.replace(/<\/?([a-z][a-z0-9]*)\b[^>]*>/gi, (match, tag) => {
      const tagName = (tag || '').toLowerCase();
      if (allowed.includes(tagName)) {
        // self-closing br handled above
        return match.charAt(1) === '/' ? `</${tagName}>` : `<${tagName}>`;
      }
      return '';
    });

    return text;
  };

  return (
    <>
      <Header />
      <main className="main__content_wrapper">
        <style>{` 
          .pd-wrap { padding: 40px 0; }
          .pd-grid { display: flex; gap: 24px; align-items: flex-start; }
          .pd-left { flex: 1 1 48%; }
          .pd-right { flex: 1 1 48%; }
          .pd-card { background: #fff; border-radius: 12px; padding: 20px; box-shadow: 0 2px 8px rgba(15,23,42,0.03); }
          .pd-image { width: 100%; aspect-ratio: 1 / 1; border-radius: 8px; object-fit: contain; background: #fff; border: 1px solid #f1f5f9; }
          .pd-thumbs { display:flex; gap:8px; margin-top:12px; }
          .pd-thumb { width:56px; height:56px; border-radius:8px; object-fit: contain; background: #fff; cursor:pointer; border:1px solid #eee; }
          .pd-title { font-size: 26px; font-weight: 600; margin:0 0 12px 0; }
          .pd-selected-variation { color: #0b63d6; font-weight: 500; }
          .pd-price-row { display:flex; align-items:center; gap:12px; margin-bottom:12px; }
          .pd-price { font-size:24px; font-weight:700; color:#111; }
          .pd-old { text-decoration: line-through; color:#9AA0A6; }
          .pd-available { font-weight:600; }
          .pd-measure { display:inline-block; margin-left:6px; padding:6px 12px; border-radius:18px; border:1px solid #e6eefc; color:#0b63d6; background:#f5fbff; }
          .pd-qty { display:flex; align-items:center; gap:8px; }
          .pd-qty button { width:40px; height:40px; border-radius:6px; border:1px solid #eceff1; background:#fff; font-weight:600; font-size:18px; cursor:pointer; }
          .pd-buy { width:100%; padding:12px 16px; border-radius:8px; border:none; background:#0b63d6; color:#fff; font-weight:700; font-size:16px; cursor:pointer; }
          .pd-section { margin-top:18px; }
          .pd-bottom { padding: 20px; }
          .pd-bottom-grid { display:flex; gap:24px; align-items:flex-start; }
          .pd-bottom-left { flex: 2 1 66%; }
          .pd-bottom-right { flex: 1 1 34%; }
          @media (max-width: 992px) { .pd-bottom-grid { flex-direction:column; } }
          .pd-features ul { padding-left:18px; margin:6px 0; }
          .pd-variations { margin-bottom:16px; }
          .pd-variation-group { margin-bottom:16px; }
          .pd-variation-label { font-weight:600; margin-bottom:8px; display:block; font-size: 16px; color: #374151; }
          .pd-variation-buttons { display:flex; flex-wrap:wrap; gap:8px; }
          .pd-variation-btn { 
            padding:12px 16px; 
            border:2px solid #e1e5e9; 
            border-radius:8px; 
            background:#fff; 
            color:#374151; 
            font-weight:500; 
            cursor:pointer; 
            transition:all 0.2s ease; 
            text-align:center;
            min-width:80px;
          }
          .pd-variation-btn:hover { border-color:#0b63d6; background:#f8fafc; }
          .pd-variation-btn.selected { 
            border-color:#0b63d6; 
            background:#0b63d6; 
            color:#fff; 
            box-shadow:0 2px 4px rgba(11,99,214,0.2);
          }
          .pd-variation-name { font-size:14px; font-weight:600; }
          .pd-variation-price { font-size:12px; color:#6b7280; margin-top:2px; }
          .pd-variation-btn.selected .pd-variation-price { color:#e0f2fe; }
          .pd-original-price { text-decoration: line-through; color:#9ca3af; margin-right:4px; }
          .pd-discounted-price { color:#ef4444; font-weight:600; }
          @media (max-width: 992px) { .pd-grid { flex-direction:column; } .pd-left,.pd-right{flex:1 1 auto;} }
        `}</style>

        <Breadcrumb title="Product Details" items={[{ label: 'Home', href: '/' }, { label: 'Product Details' }]} />
        <section className="product__details--section pd-wrap">
          <div className="container">
            {loading ? (
              <div className="row"><div className="col-12"><div style={{ padding: 40, textAlign: 'center' }}>Loading product details...</div></div></div>
            ) : error ? (
              <div className="row"><div className="col-12"><div style={{ padding: 40, color: 'red', textAlign: 'center' }}>Error: {error}</div></div></div>
            ) : product ? (
              <>
              <div className="pd-grid">
                <div className="pd-left pd-card">
                  <img className="pd-image" src={selectedImage} alt={product.name} />
                  <div className="pd-thumbs">
                    {/* Show cover + images thumbnails */}
                    {product.cover && (
                      <img 
                        className="pd-thumb" 
                        src={product.cover.startsWith('http') ? product.cover : `${API_CONFIG.IMAGE_URL}${product.cover}`}
                        alt="Main"
                        onClick={() => setSelectedImage(product.cover.startsWith('http') ? product.cover : `${API_CONFIG.IMAGE_URL}${product.cover}`)}
                      />
                    )}
                    {product.images && product.images.length > 0 && product.images.map((img, idx) => {
                      if (!img || !img.trim()) return null;
                      const imgUrl = img.startsWith('http') ? img : `${API_CONFIG.IMAGE_URL}${img}`;
                      return (
                        <img 
                          key={idx} 
                          className="pd-thumb" 
                          src={imgUrl}
                          alt={`View ${idx + 2}`}
                          onClick={() => setSelectedImage(imgUrl)}
                        />
                      );
                    })}
                  </div>
                </div>

                <div className="pd-right pd-card">
                  <h1 className="pd-title">
                    {product.name}
                    {selectedVariation && (
                      <span className="pd-selected-variation"> - {selectedVariation.name}</span>
                    )}
                  </h1>
                  <div className="pd-price-row">
                    <div>
                      {selectedVariation ? (
                        // Variant is selected - show variant price
                        <>
                          {product.discount && product.discount > 0 ? (
                            <>
                              <div className="pd-price">${formatPrice(selectedVariation.price * (1 - product.discount / 100))}</div>
                              <div className="pd-old">${formatPrice(selectedVariation.price)}</div>
                            </>
                          ) : selectedVariation.discount && selectedVariation.discount > 0 ? (
                            <>
                              <div className="pd-price">${formatPrice(selectedVariation.discount)}</div>
                              <div className="pd-old">${formatPrice(selectedVariation.price)}</div>
                            </>
                          ) : (
                            <div className="pd-price">${formatPrice(selectedVariation.price)}</div>
                          )}
                        </>
                      ) : (
                        // No variant selected - show base product price
                        <>
                          {product.discount && product.discount > 0 ? (
                            <>
                              <div className="pd-price">${formatPrice(product.sell_price)}</div>
                              <div className="pd-old">${formatPrice(product.original_price)}</div>
                            </>
                          ) : (
                            <>
                              <div className="pd-price">${formatPrice(product.sell_price || product.original_price)}</div>
                              {product.original_price && product.sell_price !== product.original_price && (
                                <div className="pd-old">${formatPrice(product.original_price)}</div>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                    <div style={{marginTop:8, fontSize:'16px', color:'#000', fontWeight:'bold'}}>
                      Total: {
                        (() => {
                          let unitPrice;
                          if (selectedVariation) {
                            // Variant selected - use variant price
                            if (product.discount && product.discount > 0) {
                              unitPrice = selectedVariation.price * (1 - product.discount / 100);
                            } else if (selectedVariation.discount && selectedVariation.discount > 0) {
                              unitPrice = selectedVariation.discount;
                            } else {
                              unitPrice = selectedVariation.price;
                            }
                          } else {
                            // No variant - use base product price
                            if (product.discount && product.discount > 0) {
                              unitPrice = product.sell_price;
                            } else {
                              unitPrice = product.sell_price || product.original_price;
                            }
                          }
                          return '$' + formatPrice(unitPrice * quantity);
                        })()
                      }
                    </div>
                  </div>

                  {variations && variations.length > 0 && variations.some(g => g.items && g.items.length > 0) && (
                    <div className="pd-section pd-variations">
                      {variations.map((variationGroup, groupIndex) => {
                        // Only render if this group has items
                        if (!variationGroup.items || variationGroup.items.length === 0) return null;
                        
                        return (
                          <div key={groupIndex} className="pd-variation-group">
                            <label className="pd-variation-label">{variationGroup.name}</label>
                            <div className="pd-variation-buttons">
                              {variationGroup.items.map((item, itemIndex) => {
                                // Variations can have their own discount field (direct price)
                                const varDiscount = parseFloat(item.discount) || 0;
                                const varPrice = parseFloat(item.price) || 0;
                                const hasVarDiscount = varDiscount > 0;
                                
                                return (
                                  <button
                                    key={itemIndex}
                                    type="button"
                                    className={`pd-variation-btn ${selectedVariation && selectedVariation.id === item.id ? 'selected' : ''}`}
                                    onClick={() => {
                                      setSelectedVariation(item);
                                      setSelectedVariantIndex(itemIndex);
                                    }}
                                  >
                                    <div className="pd-variation-name">{item.name}</div>
                                    <div className="pd-variation-price">
                                      {hasVarDiscount ? (
                                        <>
                                          <span className="pd-discounted-price">${formatPrice(varDiscount)}</span>
                                          <span className="pd-original-price">${formatPrice(varPrice)}</span>
                                        </>
                                      ) : (
                                        <span>${formatPrice(varPrice)}</span>
                                      )}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div>
                    {Number(product.in_stock) === 1 ? (
                      <span className="pd-available" style={{color:'#28a745'}}>Available</span>
                    ) : (
                      <span className="pd-available" style={{color:'#dc3545'}}>Out of stock</span>
                    )}
                    {((Number(product.have_pcs) === 1) && product.pcs && product.pcs !== "0") ? (
                      <span className="pd-measure">{product.pcs} pcs</span>
                    ) : ((Number(product.have_kg) === 1) && product.kg && product.kg !== "0") ? (
                      <span className="pd-measure">{product.kg} kg</span>
                    ) : ((Number(product.have_gram) === 1) && product.gram && product.gram !== "0") ? (
                      <span className="pd-measure">{product.gram} g</span>
                    ) : ((Number(product.have_liter) === 1) && product.liter && product.liter !== "0") ? (
                      <span className="pd-measure">{product.liter} L</span>
                    ) : ((Number(product.have_ml) === 1) && product.ml && product.ml !== "0") ? (
                      <span className="pd-measure">{product.ml} ml</span>
                    ) : null}
                  </div>

                  <div className="pd-section" style={{display:'flex', gap:12, alignItems:'center'}}>
                    {isInCart && cartItem ? (
                      <div className="pd-qty">
                        <button type="button" onClick={() => {
                          const newQuantity = cartItem.quantiy - 1;
                          if (newQuantity <= 0) {
                            cart.updateQuantity(product.id, 0); // Remove
                          } else {
                            cart.updateQuantity(product.id, newQuantity);
                          }
                        }}>-</button>
                        <div style={{minWidth:28, textAlign:'center', fontWeight:600}}>{cartItem.quantiy}</div>
                        <button type="button" onClick={() => cart.updateQuantity(product.id, cartItem.quantiy + 1)}>+</button>
                      </div>
                    ) : (
                      <>
                        <div className="pd-qty">
                          <button type="button" onClick={() => setQuantity(q => Math.max(1, q - 1))}>-</button>
                          <div style={{minWidth:28, textAlign:'center', fontWeight:600}}>{quantity}</div>
                          <button type="button" onClick={() => setQuantity(q => Math.min(99, q + 1))}>+</button>
                        </div>
                        <button 
                          className="pd-buy" 
                          onClick={async () => {
                            if (product) {
                              try {
                                // Calculate final price based on variant selection
                                let originalPrice, sellingPrice, discountValue;
                                
                                if (selectedVariation) {
                                  // Variant selected - use variant price
                                  const variantPrice = parseFloat(selectedVariation.price) || 0;
                                  const variantDiscountPrice = parseFloat(selectedVariation.discount) || 0;
                                  const productDiscountPercent = parseFloat(product.discount) || 0;
                                  
                                  originalPrice = variantPrice;
                                  
                                  if (variantDiscountPrice > 0) {
                                    // Variant has its own discounted price (direct value)
                                    sellingPrice = variantDiscountPrice;
                                    discountValue = productDiscountPercent;
                                  } else if (productDiscountPercent > 0) {
                                    // Apply product-level percent discount to variant price
                                    sellingPrice = variantPrice * (1 - productDiscountPercent / 100);
                                    discountValue = productDiscountPercent;
                                  } else {
                                    // No discounts
                                    sellingPrice = variantPrice;
                                    discountValue = 0;
                                  }

                                } else {
                                  // No variant - use base product price
                                  originalPrice = parseFloat(product.original_price) || 0;
                                  sellingPrice = parseFloat(product.sell_price) || parseFloat(product.original_price) || 0;
                                  discountValue = parseFloat(product.discount) || 0;

                                }
                                
                                // Create cart item with correct field names for CartContext
                                const cartItem = {
                                  ...product,
                                  quantiy: quantity,
                                  // Variant info
                                  selectedVariation: selectedVariation,
                                  selectedVariant: selectedVariantIndex,
                                  variant: selectedVariantIndex,
                                  // CRITICAL: Ensure variations array is included for cart display
                                  variations: variations.length > 0 ? variations : (product.variations || []),
                                  // CRITICAL: Use these exact field names - CartContext expects them
                                  original_price: originalPrice.toString(),
                                  sell_price: sellingPrice.toString(),
                                  price: sellingPrice.toString(),
                                  discount: discountValue.toString(),
                                  // Ensure size is set for variant products
                                  size: product.size || (selectedVariation ? '1' : '0'),
                                  cover: (() => {
                                    if (product.cover && typeof product.cover === 'string' && product.cover.trim()) {
                                      return product.cover;
                                    }
                                    if (product.image && typeof product.image === 'string' && product.image.trim()) {
                                      return product.image;
                                    }
                                    if (product.images) {
                                      if (Array.isArray(product.images) && product.images.length > 0) {
                                        const firstImg = product.images[0];
                                        return typeof firstImg === 'string' && firstImg.trim() ? firstImg : '';
                                      }
                                      if (typeof product.images === 'string') {
                                        try {
                                          const parsed = JSON.parse(product.images);
                                          if (Array.isArray(parsed) && parsed.length > 0) {
                                            const firstImg = parsed[0];
                                            return typeof firstImg === 'string' && firstImg.trim() ? firstImg : '';
                                          }
                                        } catch (e) {}
                                      }
                                    }
                                    return '';
                                  })()
                                };

                                
                                await cart.addItem(cartItem);
                              } catch (error) {
                                console.error('Failed to add product to cart:', error);
                              }
                            }
                          }}
                        >
                          Add to Cart
                        </button>
                      </>
                    )}
                    <button
                      className="pd-buy"
                      style={{ background: wishlist.isInWishlist(product?.id) ? '#e63946' : '#fff', color: wishlist.isInWishlist(product?.id) ? '#fff' : '#0b63d6', border: '1px solid #e63946' }}
                      onClick={() => {
                        if (wishlist.isInWishlist(product?.id)) {
                          wishlist.removeFromWishlist(product?.id);
                        } else {
                          wishlist.addToWishlist(product);
                        }
                      }}
                    >
                      {wishlist.isInWishlist(product?.id) ? 'Remove from Wishlist' : 'Add to Wishlist'}
                    </button>
                  </div>

                  
                </div>
              </div>

              <div className="pd-bottom pd-card" style={{ marginTop: 20 }}>
                {/* Description - Full Width */}
                <div style={{ marginBottom: 24 }}>
                  <h3 style={{ marginBottom: 12 }}>Description</h3>
                  <div style={{ color: '#444' }} dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(product.description) }} />
                </div>

                {/* Key Features - Full Width */}
                {product.key_features && (
                  <div className="pd-features" style={{ marginBottom: 24 }}>
                    <h3 style={{ marginBottom: 12 }}>Key Features</h3>
                    {(() => {
                      const raw = typeof product.key_features === 'string' ? product.key_features : String(product.key_features || '');
                      const cleaned = normalizeBreaks(raw);
                      // Remove any remaining HTML tags inside features
                      const cleanedNoTags = cleaned.replace(/<[^>]*>/g, '');
                      const features = cleanedNoTags.split(/,|\n/).map(f => f.trim()).filter(f => f);
                      return (
                        <ul>
                          {features.map((f, i) => (
                            <li key={i}>{f}</li>
                          ))}
                        </ul>
                      );
                    })()}
                  </div>
                )}

                {/* Disclaimer - Full Width */}
                {product.disclaimer && (
                  <div>
                    <h3 style={{ marginBottom: 12 }}>Disclaimer</h3>
                    <div style={{ color: '#666' }} dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(product.disclaimer) }} />
                  </div>
                )}
              </div>
              </>
            ) : null}
          </div>
        </section>

        {product && product.related && product.related.length > 0 && (
          <section className="product__section product__section--style3 section--padding pt-0 related__products--section">
            <div className="container">
              <div className="section__heading3 mb-40">
                <h2 className="section__heading3--maintitle">Related Products</h2>
              </div>
              <div className="product__section--inner product3__section--inner__padding product__section--style3__inner">
                <div className="row row-cols-xl-4 row-cols-lg-3 row-cols-md-2 row-cols-sm-2 row-cols-1 mb--n28" style={{ justifyContent: 'center' }}>
                  {product.related && product.related.length > 0 && product.related.slice(0, 4).map((relatedProduct) => (
                    <div key={relatedProduct.id || Math.random()} className="col mb-28 d-flex justify-content-center">
                      <ProductCard product={relatedProduct} compact />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
 
      </main>
      <Footer />
    </>
  );
};

export default ProductDetailPage;
